import { flatMap, groupBy, map, maxBy, sortBy } from "lodash";
import { FC } from "react";
import { useSelector } from "react-redux";

import { SettingsTable as Table } from "components/settings-table";
import { UNSET_DISPLAY_NAME_KEY } from "constants/settings/replyAddresses";
import { IReplyAddress } from "store/entities/reply-addresses/interfaces";
import { ITeam, selectTeamsAlphabetically } from "store/entities/teams";
import { KeyMap } from "utils/interface";

import { ShowDeleteModal, ShowEditModal, ShowPreviewModal } from "../interfaces";

import { DefaultReplyAddressEntry, ReplyAddressEntry } from "./ReplyAddressEntry";

interface IReplyAddressesListProps {
  showPreviewModal: ShowPreviewModal;
  showEditModal: ShowEditModal;
  showDeleteModal: ShowDeleteModal;
  replyAddresses: KeyMap<IReplyAddress>;
}
type ReplyAddressEntries = KeyMap<{ replyAddress: IReplyAddress; assignedTeams: KeyMap<ITeam[]> }>;

export const ReplyAddressesList: FC<IReplyAddressesListProps> = ({
  showPreviewModal,
  showDeleteModal,
  showEditModal,
  replyAddresses,
}) => {
  const teams = useSelector(selectTeamsAlphabetically);

  const nonDefaultAddresses = Object.values(replyAddresses).reduce((accumulator, replyAddress) => {
    const assignedTeams = groupBy(
      teams.filter(team => team.replyAddressID === replyAddress.id),
      team => team.replyName || UNSET_DISPLAY_NAME_KEY
    );

    const emptyAddress = Object.keys(assignedTeams).length === 0;

    return {
      ...accumulator,
      [replyAddress.id]: {
        replyAddress,
        assignedTeams: { ...assignedTeams, ...(emptyAddress ? { [UNSET_DISPLAY_NAME_KEY]: [] } : {}) },
      },
    };
  }, {}) as ReplyAddressEntries;

  const nonDefaultEntries = Object.entries(nonDefaultAddresses)
    .map(([, value]) => value)
    .sort((a, b) => {
      const updatedAtA = a.replyAddress?.updatedAt.getTime() ?? 0;
      const updatedAtB = b.replyAddress?.updatedAt.getTime() ?? 0;
      return updatedAtB - updatedAtA;
    });

  const defaultEntry = {
    replyAddress: null,
    assignedTeams: groupBy(
      teams.filter(team => team.replyAddressID === null),
      team => team.replyName || UNSET_DISPLAY_NAME_KEY
    ),
  };

  let defaultEntryAssignedTeams = sortBy(Object.keys(defaultEntry.assignedTeams), name => {
    const lastUpdatedAt = maxBy(defaultEntry.assignedTeams[name], t => t.updatedAt)?.updatedAt || -Infinity;

    return -lastUpdatedAt;
  });

  if (defaultEntryAssignedTeams.includes(UNSET_DISPLAY_NAME_KEY)) {
    const unsetIndex = defaultEntryAssignedTeams.indexOf(UNSET_DISPLAY_NAME_KEY);
    const lastElementIndex = defaultEntryAssignedTeams.length - 1;

    if (unsetIndex !== lastElementIndex) {
      defaultEntryAssignedTeams = [
        ...defaultEntryAssignedTeams.slice(0, unsetIndex),
        defaultEntryAssignedTeams[lastElementIndex],
        ...defaultEntryAssignedTeams.slice(unsetIndex + 1, lastElementIndex),
        defaultEntryAssignedTeams[unsetIndex],
      ];
    }
  }

  return (
    <Table
      columns={3}
      columnSizes="4fr 3fr 4fr"
      marginTop="0"
      marginBottom="0"
      headers={["Reply address", "Used by", "Status"]}
    >
      {flatMap(nonDefaultEntries, entry => {
        const fromNames = sortBy(Object.keys(entry.assignedTeams), name => {
          const lastUpdatedAt = maxBy(entry.assignedTeams[name], t => t.updatedAt)?.updatedAt || -Infinity;

          return -lastUpdatedAt;
        });

        return fromNames.map(fromName => (
          <ReplyAddressEntry
            replyAddress={entry.replyAddress}
            fromName={fromName === UNSET_DISPLAY_NAME_KEY ? null : fromName}
            assignedTeams={entry.assignedTeams[fromName]}
            showPreviewModal={showPreviewModal}
            showEditModal={showEditModal}
            showDeleteModal={showDeleteModal}
            key={`${fromName}-${entry?.replyAddress?.id}`}
          />
        ));
      })}
      {map(defaultEntryAssignedTeams, fromName => (
        <DefaultReplyAddressEntry
          fromName={fromName}
          assignedTeams={defaultEntry.assignedTeams[fromName]}
          showPreviewModal={showPreviewModal}
          showEditModal={showEditModal}
          showDeleteModal={showDeleteModal}
          key={`defaultReplyAddress-${fromName}`}
        />
      ))}
    </Table>
  );
};
